import {ThreeEquipment} from '../ThreeEquipment/ThreeEquipment';
import {TEquipmentCellName} from '../../../../../../common-code/types/TEquipmentCellName';
import {ThreeKUnit} from '../../units/ThreeKUnit/ThreeKUnit';
import {ISaveBuiltInEquipmentData} from '../../../../../../common-code/interfaces/saveData/ISaveBuiltInEquipmentData';
import {CommonHelper} from 'common-code';
import {
    ACTION_DELETE,
    ACTION_SETTINGS,
    ACTION_SHOW_UNIT_SPEC, SETTING_GROUP_BUILTIN_POSITION, SETTING_GROUP_COVER,
    SETTING_GROUP_GENERAL,
    SETTING_GROUP_POSITION
} from '../../../../../constants';
import {i18n} from '../../../../../i18n';
import {IContextIcon} from '../../../../../interfaces/IContextIcon';
import {Box3, Group, Mesh, Vector3} from 'three';
import {ISettingGroup} from '../../../../../interfaces/settingData/ISettingGroup';
import {ISettingGroupGeneral} from '../../../../../interfaces/settingData/ISettingGroupGeneral';
import {TSideType} from '../../../../../../common-code/types/TSideType';
import {
    SIDE_TYPE_BACK,
    SIDE_TYPE_BOTTOM,
    SIDE_TYPE_FRONT, SIDE_TYPE_LEFT, SIDE_TYPE_RIGHT,
    SIDE_TYPE_TOP
} from '../../../../../../common-code/constants';
import {ThreeUnit} from "../../ThreeUnit/ThreeUnit";
import {ISettingGroupPosition} from "../../../../../interfaces/settingData/ISettingGroupPosition";
import {TPoint2D} from "../../../../../../../../common/types/TPoint2D";
import {TPoint3D} from "../../../../../../../../common/types/TPoint3D";
import {TDeltaMovePosition} from "../../../../types/TDeltaMovePosition";
import {TOptionalPoint3D} from "../../../../../../../../common/types/TOptionalPoint3D";

export class ThreeBuiltInEquipment extends ThreeEquipment {
    saveData: ISaveBuiltInEquipmentData;
    unit: ThreeKUnit;
    hole?: Mesh;
    parentEquipment?: ThreeBuiltInEquipment;

    constructor(options: ISaveBuiltInEquipmentData, unit: ThreeKUnit) {
        super(options, unit.service);
        this.unit = unit;
        this.saveData = this.initSaveData(options);
        this.canMove = false;
        this.parentEquipment = this.initParentEquipment();
    }

    public createView(isRebuild?: boolean) {
        this.view3d.matrixAutoUpdate = false;
        if (this.coverPoints.length > 0) {
            this.createSelectCover(isRebuild);
        }
        if (this.transparentForBack) {
            this.setTransparentForBack();
        }
        this.createSticker();
        this.setPosition(this.initPosition());
        this.setRotation(this.initRotation());
        this.updateViewType();
        this.updateAllMatrices();
        this.unit.view3d.add(this.view3d);
    }

    public afterCreatView() {

    }

    public getCellName(): TEquipmentCellName {
        return this.saveData.cellName;
    }

    public getSelectColor() {
        return this.service.getDetailSelectColor();
    }

    public getSettingsGroups(): {[key: string]: ISettingGroup} {
        let groups: {[key: string]: ISettingGroup};
        let generalGroup: ISettingGroup | undefined;
        let generalGroupData: ISettingGroupGeneral | undefined;
        let builtInPosition: ISettingGroup | undefined;

        groups = super.getSettingsGroups();
        generalGroup = groups[SETTING_GROUP_GENERAL];
        if (generalGroup && generalGroup.data) {
            generalGroupData = generalGroup.data as ISettingGroupGeneral;
        }
        if (generalGroupData) {
            delete generalGroupData.isStick;
            delete generalGroupData.isDimensions;
            delete generalGroupData.isLevelStick;
            delete generalGroupData.isWallStick;
            delete generalGroupData.selfVerticalRotation;
            generalGroupData.parentUnit = this.unit.getId();
        }
        delete groups[SETTING_GROUP_POSITION];
        delete groups[SETTING_GROUP_COVER];
        builtInPosition = this.getSettingsBuiltInPosition();
        if (builtInPosition) {
            groups[SETTING_GROUP_BUILTIN_POSITION] = builtInPosition;
        }

        return groups;
    }

    public getData(): ISaveBuiltInEquipmentData {
        let data: ISaveBuiltInEquipmentData = CommonHelper.deepCopy(super.getData());

        delete data.isStick;
        delete data.isWallStick;
        delete data.isLevelStick;

        return data;
    }

    public getContextIcons(): IContextIcon[] {
        let icons: IContextIcon[];
        let actionData = this.actionData();

        icons = [
            {
                channelName: 'ThreeBuiltInEquipment',
                action: ACTION_SETTINGS,
                actionData: actionData,
                popup: true,
                icon: "settings-object",
                hide: false,
                title: i18n.t("Свойства"),
                sort: 100,
            },
            {
                channelName: 'ThreeBuiltInEquipment',
                action: ACTION_SHOW_UNIT_SPEC,
                actionData: actionData,
                popup: true,
                icon: "list",
                hide: false,
                title: i18n.t("Спецификация"),
                sort: 130,
            },
            {
                channelName: 'ThreeBuiltInEquipment',
                action: ACTION_DELETE,
                actionData: actionData,
                popup: false,
                icon: 'delete',
                hide: true,
                title: i18n.t('Удалить'),
                sort: 500
            }
        ];

        return icons;
    }

    public getParentUnitId(): number | undefined {
        return this.unit.getId();
    }

    public getParentUnit(): ThreeUnit | undefined {
        return this.unit;
    }

    public getChildEquipmentPosition(_cellName: TEquipmentCellName): TPoint3D {
        return {x: 0, y: 0, z: 0};
    }

    public trySetPosition(moveData: TDeltaMovePosition, isFinal?: boolean, isStick?: boolean): boolean {
        if (this.cover.position.equals(moveData.position) && !isFinal) {
            return true;
        }
        this.view3d.position.copy(moveData.position);
        this.updateAllMatrices();
        this.trySetChildrenPosition();
        this.updateSaveData();

        return true;
    }

    public calculateSideMovePositionByDelta(
        newPosition: Vector3,
        delta: number,
        sideType: TSideType
    ) {
        switch (sideType) {
            case SIDE_TYPE_BOTTOM:
                newPosition.y -= delta;
                break;
            case SIDE_TYPE_TOP:
                newPosition.y += delta;
                break;
            case SIDE_TYPE_BACK:
                newPosition.z -= delta;
                break;
            case SIDE_TYPE_FRONT:
                newPosition.z += delta;
                break;
            case SIDE_TYPE_RIGHT:
                newPosition.x += delta;
                break;
            case SIDE_TYPE_LEFT:
                newPosition.x -= delta;
                break;
        }
    }

    protected initSaveData(options: ISaveBuiltInEquipmentData): ISaveBuiltInEquipmentData {
        return super.initSaveData(options) as ISaveBuiltInEquipmentData;
    }

    protected setView3dName() {
        this.view3d.name = 'ThreeBuiltInEquipment';
    }

    protected initParentEquipment(): ThreeBuiltInEquipment | undefined {
        return undefined;
    }

    protected trySetChildrenPosition() {
        let child: ThreeBuiltInEquipment;

        for (child of this.unit.equipments) {
            if (child.parentEquipment && child.parentEquipment.getId() === this.getId()) {
                child.clearSaveDataPosition();
                child.setPosition(child.initPosition());
            }
        }
    }

    protected getSettingsBuiltInPosition(): ISettingGroup | undefined {
        let positionData: ISettingGroupPosition | undefined;

        positionData = this.getSettingsBuiltInPositionData();
        if (!positionData) {
            return undefined;
        }
        return {
            id: SETTING_GROUP_BUILTIN_POSITION,
            title: i18n.t('Отступ при установке'),
            data: positionData,
        }
    }

    protected getSettingsBuiltInPositionData(): ISettingGroupPosition | undefined {
        return undefined;
    }

    protected getSinkFaucetPosition(): TPoint3D {
        let result: TPoint3D;
        let direction: -1 | 1;
        const position: TOptionalPoint3D | undefined = this.threeModelData?.faucetPosition;

        direction = this.isFlipX() ? -1 : 1;
        result = {x: 0, y: 0, z: 0};
        if (position && position.x) {
            result.x = direction * position.x;
        }
        if (position && position.y) {
            result.y = position.y;
        }
        if (position && position.z) {
            result.z = position.z;
        }

        return result;
    }

    protected createSelectCover(isRebuild?: boolean) {
        super.createSelectCover(isRebuild);
        this.selectCover.userData.commonObject = this;
    }

    protected setTabletopHole() {
        this.threeModel.traverse(child => {
            if (child instanceof Mesh) {
                if (child.name === 'hole') {
                    child.material.colorWrite = false;
                    child.renderOrder = 1;
                    child.userData.notSwitchView = true;
                    child.userData.notCreateCarcass = true;
                    this.hole = child;
                }
            }
        })
        if (!this.hole) {
            const hole: Mesh | undefined = this.createTabletopHole(this.threeModel);
            if (hole) {
                this.threeModel.add(hole);
                this.hole = hole;
            }
        }
    }

    protected createTabletopHole(threeModel: Group): Mesh | undefined {
        console.log('Need create hole ', threeModel.name);
        return undefined;
        // let hole: Mesh | undefined;
        // let resultGeometry: BufferGeometry = new BufferGeometry();
        // try {
        //     threeModel.traverse(child => {
        //         if (child instanceof Mesh) {
        //             resultGeometry = child.geometry.clone().merge(resultGeometry);
        //         }
        //     })
        //     resultGeometry.scale(0.99, 0.99, 0.99);
        //     hole = new Mesh(resultGeometry, new MeshBasicMaterial());
        //     (hole.material as MeshBasicMaterial).colorWrite = false;
        //     hole.renderOrder = 1;
        //     hole.userData.notSwitchView = true;
        //     hole.userData.notCreateCarcass = true;
        // } catch (e) {
        //     debugger;
        //     return undefined;
        // }
        //
        // return hole;
    }

    protected getSettingsBuiltInHorizontalPositionData(): ISettingGroupPosition | undefined {
        let coverBox: Box3;
        let unitBox: Box3;
        let currentPolygon: TPoint2D[];
        let unitPolygon: TPoint2D[];

        coverBox = this.getCoverBox(0);
        this.unit.view3d.worldToLocal(coverBox.min.applyMatrix4(this.selectCover.matrixWorld));
        this.unit.view3d.worldToLocal(coverBox.max.applyMatrix4(this.selectCover.matrixWorld));
        unitBox = this.unit.getCoverBox(0);
        currentPolygon = [
            {x: +coverBox.min.x.toFixed(3), y: +coverBox.min.z.toFixed(3)},
            {x: +coverBox.max.x.toFixed(3), y: +coverBox.min.z.toFixed(3)},
            {x: +coverBox.max.x.toFixed(3), y: +coverBox.max.z.toFixed(3)},
            {x: +coverBox.min.x.toFixed(3), y: +coverBox.max.z.toFixed(3)},
        ];
        unitPolygon = [
            {x: +unitBox.min.x.toFixed(3), y: +unitBox.min.z.toFixed(3)},
            {x: +unitBox.max.x.toFixed(3), y: +unitBox.min.z.toFixed(3)},
            {x: +unitBox.max.x.toFixed(3), y: +unitBox.max.z.toFixed(3)},
            {x: +unitBox.min.x.toFixed(3), y: +unitBox.max.z.toFixed(3)},
        ];

        return {
            back: this.calculateHorizontalPositionInterval(currentPolygon, unitPolygon, SIDE_TYPE_BACK),
            front: this.calculateHorizontalPositionInterval(currentPolygon, unitPolygon, SIDE_TYPE_FRONT),
            left: this.calculateHorizontalPositionInterval(currentPolygon, unitPolygon, SIDE_TYPE_LEFT),
            right: this.calculateHorizontalPositionInterval(currentPolygon, unitPolygon, SIDE_TYPE_RIGHT),
        }
    }

}